import { useMemo } from 'react';
import dayjs from 'dayjs';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { StoneXGrid } from '../../../StoneX';
import { StoneXNumber, StoneXNumberFormat } from '../../../StoneXMui';
import { MatrixTerm } from '../../../../Generated/Mark-It-View-Api';
import { MatrixTermLabels } from './MatrixLabels';
import { MatrixGraphPoint, MatrixTermResult } from './MatrixTypes';

interface MatrixTableProps {
  results: MatrixTermResult[];
  graphPoints: MatrixGraphPoint[];
  weighted: boolean;
  historyMode: boolean;
  selectedPercentiles: number[];
}

function getDecileValue(deciles: { rank: number; value: number }[], rank: number): number {
  const decile = deciles.find(d => Math.abs(d.rank - rank) < 0.001);
  return decile?.value ?? 0;
}

interface HistoryRow {
  quoteDate: string;
  mean: number;
  [key: string]: any;
}

interface BandRow {
  band: string;
  [key: string]: any;
}

function normalizeSeason(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const matrixTermOrder: MatrixTerm[] = [
  MatrixTerm.Annual,
  MatrixTerm.Summer,
  MatrixTerm.Winter,
  MatrixTerm.FirstQuarter,
  MatrixTerm.SecondQuarter,
  MatrixTerm.ThirdQuarter,
  MatrixTerm.FourthQuarter,
  MatrixTerm.JanuaryFebruary,
  MatrixTerm.MarchApril,
  MatrixTerm.JulyAugust,
  MatrixTerm.OctoberDecember,
  MatrixTerm.Custom,
];

const seasonSortOrder = new Map<string, number>();
for (let i = 0; i < matrixTermOrder.length; i++) {
  const term = matrixTermOrder[i];
  seasonSortOrder.set(normalizeSeason(term), i);
  seasonSortOrder.set(normalizeSeason(MatrixTermLabels[term]), i);
}

function getSeasonSortIndex(season: string): number {
  return seasonSortOrder.get(normalizeSeason(season)) ?? Number.MAX_SAFE_INTEGER;
}

export default function MatrixTable({ results, graphPoints, weighted, historyMode, selectedPercentiles }: MatrixTableProps) {
  const sortedDeciles = useMemo(() => [...selectedPercentiles].sort((a, b) => a - b), [selectedPercentiles]);
  const sortedResults = useMemo(
    () => [...results].sort((a, b) => getSeasonSortIndex(a.season) - getSeasonSortIndex(b.season) || a.season.localeCompare(b.season)),
    [results],
  );

  const historyRowsByTerm = useMemo<Map<string, HistoryRow[]>>(() => {
    if (!historyMode || graphPoints.length === 0) return new Map();
    const map = new Map<string, HistoryRow[]>();
    for (const point of graphPoints) {
      for (const term of point.terms) {
        if (!map.has(term.season)) {
          map.set(term.season, []);
        }
        const row: HistoryRow = {
          quoteDate: point.calculationDate,
          mean: term.weightedMean,
        };
        for (const decile of sortedDeciles) {
          row[`p${decile}`] = getDecileValue(term.weightedDeciles, decile / 100);
        }
        map.get(term.season)!.push(row);
      }
    }
    return map;
  }, [graphPoints, historyMode, sortedDeciles]);

  const historyColumns = useMemo<ColDef<HistoryRow>[]>(() => {
    const columns: ColDef<HistoryRow>[] = [
      { headerName: 'Quote Date', field: 'quoteDate', flex: 1, valueFormatter: (params) => dayjs(params.value).format('MM/DD/YYYY') },
      { headerName: 'Mean', field: 'mean', flex: 1, cellRenderer: (params: ICellRendererParams<HistoryRow>) => <StoneXNumber number={params.value ?? undefined} decimals={4} /> },
    ];
    for (const decile of sortedDeciles) {
      columns.push({
        headerName: `${decile}th`,
        field: `p${decile}`,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<HistoryRow>) => <StoneXNumber number={params.value ?? undefined} decimals={4} />,
      });
    }
    return columns;
  }, [sortedDeciles]);

  const bandRows = useMemo<BandRow[]>(() => {
    if (historyMode || results.length === 0) return [];

    const rows: BandRow[] = [];

    // Mean row
    const meanRow: BandRow = { band: 'Mean' };
    for (const result of sortedResults) {
      meanRow[result.season] = StoneXNumberFormat({ number: result.weightedMean, decimals: 4 });
    }
    rows.push(meanRow);

    // Median row
    const medianRow: BandRow = { band: 'Median' };
    for (const result of sortedResults) {
      medianRow[result.season] = StoneXNumberFormat({ number: getDecileValue(result.weightedDeciles, 0.5), decimals: 4 });
    }
    rows.push(medianRow);

    // Dynamic band rows from selectedPercentiles (top to bottom)
    for (let i = sortedDeciles.length - 2; i >= 0; i--) {
      const low = sortedDeciles[i] / 100;
      const high = sortedDeciles[i + 1] / 100;
      const label = `${sortedDeciles[i]}% - ${sortedDeciles[i + 1]}%`;
      const row: BandRow = { band: label };
      for (const result of sortedResults) {
        const lowValue = getDecileValue(result.weightedDeciles, low);
        const highValue = getDecileValue(result.weightedDeciles, high);
        row[result.season] = `${StoneXNumberFormat({ number: lowValue, decimals: 4 })} — ${StoneXNumberFormat({ number: highValue, decimals: 4 })}`;
      }
      rows.push(row);
    }

    // Price count row
    const countRow: BandRow = { band: 'Price Count' };
    for (const result of sortedResults) {
      countRow[result.season] = weighted
        ? `${result.innerPriceCount} / ${result.outerPriceCount}`
        : `${result.outerPriceCount}`;
    }
    rows.push(countRow);

    return rows;
  }, [sortedResults, historyMode, weighted, sortedDeciles]);

  const bandColumns = useMemo<ColDef<BandRow>[]>(() => {
    const columns: ColDef<BandRow>[] = [
      { headerName: 'Band', field: 'band', flex: 1, pinned: 'left' },
    ];
    for (const result of sortedResults) {
      columns.push({
        headerName: result.season,
        field: result.season,
        flex: 1,
      });
    }
    return columns;
  }, [sortedResults]);

  if (historyMode) {
    if (historyRowsByTerm.size === 0) return null;
    return (
      <>
        {Array.from(historyRowsByTerm.entries()).map(([season, rows]) => (
          <div key={season}>
            {historyRowsByTerm.size > 1 && <h4 style={{ margin: '8px 0 4px' }}>{season}</h4>}
            <StoneXGrid rows={rows} columns={historyColumns} hideGridOptions />
          </div>
        ))}
      </>
    );
  }

  if (bandRows.length === 0) return null;
  return <StoneXGrid rows={bandRows} columns={bandColumns} hideGridOptions />;
}
